import type { MonthKey } from '../exportsPage.types';

const MONTH_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Verifica se a string está no formato `AAAA-MM` com mês válido. */
export function isValidMonthKey(value: string | null | undefined): value is MonthKey {
    return typeof value === 'string' && MONTH_KEY_PATTERN.test(value);
}

/**
 * Converte uma data para `AAAA-MM` no fuso local — mesmo critério usado nas
 * telas de alocação, que exibem datas com `toLocaleDateString('pt-BR')`.
 */
export function toMonthKey(date: Date): MonthKey {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}`;
}

/** Desloca um mês `AAAA-MM` em `amount` meses (aceita valores negativos). */
export function addMonths(monthKey: MonthKey, amount: number): MonthKey {
    const [year, month] = monthKey.split('-').map(Number);
    return toMonthKey(new Date(year, month - 1 + amount, 1));
}

/**
 * Lista todos os meses do intervalo fechado `[start, end]`, em ordem crescente.
 * Retorna vazio se o intervalo for inválido ou invertido.
 */
export function buildMonthKeyList(start: MonthKey, end: MonthKey): MonthKey[] {
    if (!isValidMonthKey(start) || !isValidMonthKey(end) || start > end) {
        return [];
    }

    const months: MonthKey[] = [];
    let current = start;
    while (current <= end) {
        months.push(current);
        current = addMonths(current, 1);
    }
    return months;
}

/** Formata `AAAA-MM` como `mar/2025`, para uso em rótulos de tela. */
export function formatMonthLabel(monthKey: MonthKey): string {
    if (!isValidMonthKey(monthKey)) {
        return monthKey;
    }
    const [year, month] = monthKey.split('-').map(Number);
    return new Date(year, month - 1, 1)
        .toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
        .replace('.', '');
}
